import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { Sun, Loader2, FileText, Ligature as FileSignature, PlusCircle, History, BarChart3, ChevronDown, Shield } from 'lucide-react';
import { formProgressAtom } from './store/form';
import ExcelImportExport from './components/ExcelImportExport';
import CustomerSearch from './components/CustomerSearch';
import { useQuery } from 'react-query';
import { useEffect, useState, useRef } from 'react';
import { supabase } from './lib/supabase';
import AuthForm from './components/AuthForm';
import ContractFlow from './components/ContractFlow.tsx';
import PowerOfAttorneyFlow from './components/PowerOfAttorneyFlow';
import HistoryView from './components/HistoryView';
import LandingPage from './components/LandingPage';
import DataRecoveryPanel from './components/DataRecoveryPanel';
import ReportsView from './components/ReportsView';
import AdminUnlockPanel from './components/AdminUnlockPanel';
import { contractLockService } from './services/contractLockService';

function App() {
  const [formProgress, setFormProgress] = useAtom(formProgressAtom);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFlow, setSelectedFlow] = useState<'contract' | 'power-of-attorney' | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const [historyViewType, setHistoryViewType] = useState<'contract' | 'power-of-attorney' | null>(null);
  const historyDropdownRef = useRef<HTMLDivElement>(null);

  const { data: userRole } = useQuery(
    ['user-role', session?.user?.id],
    async () => {
      if (!session?.user?.id) return null;
      return contractLockService.getUserRole(session.user.id);
    },
    {
      enabled: !!session?.user?.id,
      staleTime: 300000
    }
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (historyDropdownRef.current && !historyDropdownRef.current.contains(event.target as Node)) {
        setShowHistoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={!session ? <LandingPage /> : <Navigate to="/app" />} />
      <Route path="/login" element={!session ? <AuthForm /> : <Navigate to="/app" />} />
      <Route path="/register" element={!session ? <AuthForm isSignUp /> : <Navigate to="/app" />} />
      <Route
        path="/app/*"
        element={
          session ? (
            <div className="min-h-screen bg-gray-50">
              <header className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <img
                        src="https://imgur.com/DT6IwWn.jpg"
                        alt="Logo"
                        onClick={() => setSelectedFlow(null)}
                        className="h-8 cursor-pointer hover:opacity-80 transition-opacity mr-3"
                      />
                      <div className="flex flex-col">
                        <h1 className="text-xl font-bold text-gray-900">
                          <span className="block md:hidden text-sm">Meu Contrato Solar</span>
                          <span className="hidden md:block">Meu Contrato Solar</span>
                        </h1>
                        <span className="text-sm text-gray-600">
                          Gerador de Contratos de Energia Solar
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {userRole?.role === 'admin' && (
                        <button
                          onClick={() => {
                            setShowAdminPanel(true);
                            setSelectedFlow(null);
                            setShowReports(false);
                            setHistoryViewType(null);
                          }}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 flex items-center gap-2"
                        >
                          <Shield className="w-4 h-4" />
                          <span className="hidden sm:inline">Admin</span>
                        </button>
                      )}
                      <div className="relative" ref={historyDropdownRef}>
                        <button
                          onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center gap-2"
                        >
                          <History className="w-4 h-4" />
                          <span className="hidden sm:inline">Historico</span>
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        {showHistoryDropdown && (
                          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border z-50">
                            <div className="py-2">
                              <button
                                onClick={() => {
                                  setHistoryViewType('contract');
                                  setSelectedFlow(null);
                                  setShowReports(false);
                                  setShowHistoryDropdown(false);
                                }}
                                className="w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center gap-3 transition-colors"
                              >
                                <FileText className="w-5 h-5 text-blue-600" />
                                <div>
                                  <div className="font-medium text-gray-900">Contratos</div>
                                  <div className="text-xs text-gray-500">Ver historico de contratos</div>
                                </div>
                              </button>
                              <button
                                onClick={() => {
                                  setHistoryViewType('power-of-attorney');
                                  setSelectedFlow(null);
                                  setShowReports(false);
                                  setShowHistoryDropdown(false);
                                }}
                                className="w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center gap-3 transition-colors"
                              >
                                <FileSignature className="w-5 h-5 text-green-600" />
                                <div>
                                  <div className="font-medium text-gray-900">Procuracoes</div>
                                  <div className="text-xs text-gray-500">Ver historico de procuracoes</div>
                                </div>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => supabase.auth.signOut()}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      >
                        Sair
                      </button>
                    </div>
                  </div>
                </div>
              </header>

              <main className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
                {showAdminPanel ? (
                  <div className="space-y-6">
                    <button
                      onClick={() => setShowAdminPanel(false)}
                      className="text-gray-600 hover:text-gray-900 flex items-center"
                    >
                      ← Voltar
                    </button>
                    <AdminUnlockPanel />
                  </div>
                ) : historyViewType ? (
                  <div className="space-y-6">
                    <button
                      onClick={() => setHistoryViewType(null)}
                      className="text-gray-600 hover:text-gray-900 flex items-center"
                    >
                      ← Voltar
                    </button>
                    <div className="bg-white rounded-lg shadow-lg p-6">
                      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                        {historyViewType === 'contract' ? (
                          <>
                            <FileText className="w-6 h-6 text-blue-600" />
                            Historico de Contratos
                          </>
                        ) : (
                          <>
                            <FileSignature className="w-6 h-6 text-green-600" />
                            Historico de Procuracoes
                          </>
                        )}
                      </h2>
                      <HistoryView
                        type={historyViewType}
                        onSelect={() => {
                          setSelectedFlow(historyViewType);
                          setHistoryViewType(null);
                          setShowHistory(false);
                        }}
                      />
                    </div>
                  </div>
                ) : showReports ? (
                  <div className="space-y-6">
                    <button
                      onClick={() => setShowReports(false)}
                      className="text-gray-600 hover:text-gray-900 flex items-center"
                    >
                      ← Voltar
                    </button>
                    <ReportsView />
                  </div>
                ) : !selectedFlow ? (
                  <div className="flex flex-col items-center justify-center gap-8">
                    <div className="text-center">
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">
                        Selecione uma opção
                      </h2>
                      <p className="text-gray-600">
                        Escolha entre gerar documentos ou visualizar relatórios
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 w-full max-w-4xl">
                      <button
                        onClick={() => {
                          localStorage.removeItem('formProgress');
                          setFormProgress({
                            currentStep: 'customer-info',
                            completedSteps: [],
                            data: {}
                          });
                          setResetKey(prev => prev + 1);
                          setSelectedFlow('contract');
                        }}
                        className="flex flex-col items-center justify-center px-6 py-8 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                      >
                        <FileText className="w-12 h-12 mb-3" />
                        <div className="text-center">
                          <div className="font-semibold text-lg mb-1">Contrato + Procuração</div>
                          <p className="text-sm opacity-90">Gerar documentos completos</p>
                        </div>
                      </button>

                      <button
                        onClick={() => {
                          localStorage.removeItem('formProgress');
                          setFormProgress({
                            currentStep: null,
                            completedSteps: [],
                            data: {
                              powerOfAttorney: null
                            }
                          });
                          setResetKey(prev => prev + 1);
                          setSelectedFlow('power-of-attorney');
                        }}
                        className="flex flex-col items-center justify-center px-6 py-8 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
                      >
                        <FileSignature className="w-12 h-12 mb-3" />
                        <div className="text-center">
                          <div className="font-semibold text-lg mb-1">Procuração</div>
                          <p className="text-sm opacity-90">Gerar apenas procuração</p>
                        </div>
                      </button>

                      <button
                        onClick={() => setShowReports(true)}
                        className="flex flex-col items-center justify-center px-6 py-8 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors"
                      >
                        <BarChart3 className="w-12 h-12 mb-3" />
                        <div className="text-center">
                          <div className="font-semibold text-lg mb-1">Relatórios</div>
                          <p className="text-sm opacity-90">Visualizar vendas e métricas</p>
                        </div>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <DataRecoveryPanel />
                    <div className="mb-6 flex items-center justify-between">
                      <button
                        onClick={() => setSelectedFlow(null)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        ← Voltar
                      </button>
                      <div className="flex items-center gap-4">
                        {showHistory && <CustomerSearch documentType={selectedFlow} />}
                        {selectedFlow === 'power-of-attorney' && (
                          <button
                            onClick={() => {
                              localStorage.removeItem('formProgress');
                              setFormProgress({
                                currentStep: null,
                                completedSteps: [],
                                data: {
                                  powerOfAttorney: null
                                }
                              });
                              setResetKey(prev => prev + 1);
                              setShowHistory(false);
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center"
                          >
                            <PlusCircle className="w-4 h-4 mr-2" />
                            Nova Procuração
                          </button>
                        )}
                        {selectedFlow === 'contract' && (
                          <button
                            onClick={() => {
                              localStorage.removeItem('formProgress');
                              setFormProgress({
                                currentStep: 'customer-info',
                                completedSteps: [],
                                data: {}
                              });
                              setResetKey(prev => prev + 1);
                              setShowHistory(false);
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center"
                          >
                            <PlusCircle className="w-4 h-4 mr-2" />
                            Novo Cadastro
                          </button>
                        )}
                        <button
                          onClick={() => setShowHistory(true)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
                        >
                          <History className="w-4 h-4 mr-2" />
                          Histórico
                        </button>
                      </div>
                    </div>

                    {selectedFlow === 'contract' ? (
                      showHistory ? (
                        <HistoryView type={selectedFlow} onSelect={() => setShowHistory(false)} />
                      ) : (
                        <ContractFlow key={resetKey} />
                      )
                    ) : (
                      showHistory ? (
                        <HistoryView type={selectedFlow} onSelect={() => setShowHistory(false)} />
                      ) : (
                        <PowerOfAttorneyFlow key={resetKey} />
                      )
                    )}
                  </div>
                )}
              </main>
            </div>
          ) : (
            <Navigate to="/login" />
          )
        }
      />
    </Routes>
  );
}

export default App;